/* =====================================================================
 * @auw/tenant — Zod schema for TenantManifest.
 *
 * Guards every manifest at parse time; values that satisfy the schema
 * are cast to the public `TenantManifest` interface without further
 * runtime work.  The `features` map is intentionally permissive
 * (`record<string, boolean>`) so adding a new FeatureId in
 * `@auw/feature-flags` does not force every tenant manifest file to
 * be republished — the TypeScript type is the source of truth for
 * the allowed id set.
 * ===================================================================== */

import { z } from 'zod';

const TenantLocaleSchema = z.object({
  default: z.string().min(2),
  alternates: z.array(z.string().min(2)).readonly().default([]),
});

const TenantIdentitySchema = z.object({
  appName: z.string().min(1),
  siteUrl: z.string().url(),
  siteName: z.string().min(1),
  locale: TenantLocaleSchema,
});

/**
 * Asset font config is intentionally permissive — any string value is
 * accepted so tenants can point either at a third-party CDN (SaaS B2C)
 * or a self-hosted relative path (on-prem). Empty strings are allowed
 * and mean "do not load this slot" at runtime.
 */
const TenantAssetFontsSchema = z
  .object({
    bodyHref: z.string().optional(),
    monospaceHref: z.string().optional(),
    externalRegistryEnabled: z.boolean().optional(),
  })
  .optional();

/**
 * Per-mode visual override for the sidebar avatar bubble.
 *
 * Phase 12 introduces an optional image surface for the workspace
 * mode buttons in the main sidebar. When `avatar` is missing, the
 * sidebar falls back to the legacy letter+colour-dot rendering driven
 * by `WORKSPACE_MODES` in `@auw/app-shell`. Tenants that want a
 * branded chip drop a path here.
 */
const TenantWorkspaceModeAssetsSchema = z
  .object({
    avatar: z.string().min(1).optional(),
    avatarAlt: z.string().min(1).optional(),
  })
  .partial()
  .optional();

const TenantWorkspaceModeAssetsMapSchema = z
  .object({
    agent: TenantWorkspaceModeAssetsSchema,
  })
  .partial()
  .optional();

/**
 * System wallpaper paths the settings modal exposes as the "다크 / 라이트"
 * pair. Both fields are optional — missing keys fall back to the
 * default `${BASE_URL}assets/system_bg_*.webp` paths used by the B2C
 * build.
 */
const TenantWallpaperAssetsSchema = z
  .object({
    systemDark: z.string().min(1).optional(),
    systemLight: z.string().min(1).optional(),
  })
  .partial()
  .optional();

/**
 * Skill modal surface — currently the single hero/avatar shown on
 * each reference card. Adding more skill-related visuals later (e.g.
 * category icons) should land in this bag rather than spreading
 * across new top-level fields.
 */
const TenantSkillAssetsSchema = z
  .object({
    referenceCardLogo: z.string().min(1).optional(),
    referenceCardAlt: z.string().min(1).optional(),
  })
  .partial()
  .optional();

const TenantAssetsSchema = z.object({
  favicon: z.string().min(1),
  agentLogo: z.string().min(1),
  agentLogoWebp: z.string().min(1).optional(),
  appNameLogo: z.string().min(1).optional(),
  /**
   * Optional dark-theme variant of `appNameLogo`. Wordmarks tend to be
   * monochrome (black on light, white on dark), so providing the same
   * file for both themes wrecks contrast in the opposite mode.
   * `resolveAppNameLogo()` returns `null` in dark mode when this is
   * absent so callers can hide the wordmark instead of rendering an
   * unreadable variant.
   */
  appNameLogoDark: z.string().min(1).optional(),
  defaultOgImage: z.string().min(1),
  fonts: TenantAssetFontsSchema,
  pdfWorkerUrl: z.string().min(1).optional(),
  pdfCMapUrl: z.string().min(1).optional(),
  pdfStandardFontDataUrl: z.string().min(1).optional(),
  pdfWasmUrl: z.string().min(1).optional(),
  workspaceModes: TenantWorkspaceModeAssetsMapSchema,
  wallpapers: TenantWallpaperAssetsSchema,
  skills: TenantSkillAssetsSchema,
});

const TenantFeaturesSchema = z.record(z.string(), z.boolean());

/**
 * Workspace overrides are permissive on purpose — we validate the
 * mode-id union at *resolve* time with `isWorkspaceMode` rather than
 * here, so that adding a new workspace mode in `@auw/app-shell` does
 * not require republishing every tenant manifest. The schema only
 * enforces shape (strings, readable literals) plus trivial sanity.
 */
const TenantWorkspaceSchema = z
  .object({
    modeOrder: z.array(z.string().min(1)).optional(),
    defaultMode: z.string().min(1).optional(),
    defaultPath: z.string().min(1).optional(),
  })
  .optional();

/**
 * Plugin overrides are also permissive: we don't validate against the
 * builtin plugin id list here because enterprise tenants may ship
 * custom MCP connectors whose ids are not known to `@auw/plugins`.
 * `resolvePluginCatalog()` is responsible for merging the builtin
 * descriptor and the override at runtime.
 */
const TenantPluginMetaSchema = z
  .object({
    logoSrc: z.string().min(1).optional(),
    logoAlt: z.string().min(1).optional(),
    accentColor: z.string().min(1).optional(),
    accentBackground: z.string().min(1).optional(),
  })
  .partial();

const TenantPluginCatalogSchema = z
  .object({
    categoryKey: z.string().min(1).nullable().optional(),
    featuresKey: z.string().min(1).nullable().optional(),
    descriptionKey: z.string().min(1).optional(),
    developer: z.string().min(1).nullable().optional(),
    websiteUrl: z.string().url().nullable().optional(),
    privacyPolicyUrl: z.string().url().nullable().optional(),
  })
  .partial();

const TenantPluginUxSchema = z
  .object({
    connectionMode: z
      .enum(['oauth', 'api_key', 'tenant_identity', 'managed', 'none'])
      .optional(),
    statusLabel: z.string().min(1).optional(),
    statusLabelKey: z.string().min(1).optional(),
    detailNotice: z.string().min(1).optional(),
    detailNoticeKey: z.string().min(1).optional(),
    hideConnectActions: z.boolean().optional(),
    hideVerifyAction: z.boolean().optional(),
  })
  .partial();

const TenantPluginOverrideSchema = z
  .object({
    hidden: z.boolean().optional(),
    label: z.string().min(1).optional(),
    meta: TenantPluginMetaSchema.optional(),
    catalog: TenantPluginCatalogSchema.optional(),
    ux: TenantPluginUxSchema.optional(),
    category: z.string().min(1).optional(),
  })
  .partial();

const TenantPluginsSchema = z
  .object({
    enabled: z.boolean().optional(),
    hiddenIds: z.array(z.string().min(1)).optional(),
    order: z.array(z.string().min(1)).optional(),
    overrides: z.record(z.string().min(1), TenantPluginOverrideSchema).optional(),
  })
  .optional();

/**
 * Auth provider kinds are constrained to the union known to
 * `@auw/auth`. Adding a new kind is a deliberate cross-package
 * change (new factory + schema bump) so the union stays closed here
 * rather than following the permissive pattern we use for plugin ids.
 */
const TenantAuthProviderKindSchema = z.enum([
  'google_oauth',
  'saml',
  'oidc',
  'email_password',
  'knox',
]);

const TenantAuthProviderEntrySchema = z.object({
  id: z.string().min(1),
  kind: TenantAuthProviderKindSchema,
  labelKey: z.string().min(1).optional(),
  iconSrc: z.string().min(1).optional(),
});

const TenantAuthSchema = z
  .object({
    providers: z.array(TenantAuthProviderEntrySchema).optional(),
    defaultProviderId: z.string().min(1).optional(),
    inviteRedirectPath: z.string().min(1).optional(),
  })
  .optional();

/**
 * Role grants are either the `'*'` super-role sentinel or an explicit
 * list of permission strings. Permission strings stay permissive
 * because enterprise tenants may define custom permissions scoped to
 * their own admin feature packs (see `@auw/permissions`).
 */
const RolePolicySchema = z.object({
  grants: z.union([
    z.literal('*'),
    z.array(z.string().min(1)),
  ]),
});

const RoleDefinitionSchema = z.object({
  id: z.string().min(1),
  labelKey: z.string().min(1).optional(),
  descriptionKey: z.string().min(1).optional(),
  policy: RolePolicySchema,
});

const TenantRolesSchema = z
  .object({
    definitions: z.array(RoleDefinitionSchema),
    defaultRoleId: z.string().min(1).optional(),
  })
  .optional();

/**
 * Analytics & payments toggles. The `enabled` field is the primary
 * gate; per-provider detail fields (measurementId / dsn / clientToken)
 * stay optional because the runtime loader reads env-var fallbacks to
 * keep the legacy B2C wiring intact.
 */
const TenantAnalyticsGa4Schema = z.object({
  enabled: z.boolean(),
  measurementId: z.string().min(1).optional(),
});

const TenantAnalyticsSentrySchema = z.object({
  enabled: z.boolean(),
  dsn: z.string().min(1).optional(),
  allowUrls: z.array(z.string().min(1)).optional(),
});

const TenantAnalyticsSchema = z
  .object({
    ga4: TenantAnalyticsGa4Schema.optional(),
    sentry: TenantAnalyticsSentrySchema.optional(),
  })
  .optional();

const TenantPaymentsPaddleSchema = z.object({
  enabled: z.boolean(),
  clientToken: z.string().min(1).optional(),
  environment: z.enum(['sandbox', 'production']).optional(),
});

const TenantPaymentsSchema = z
  .object({
    paddle: TenantPaymentsPaddleSchema.optional(),
  })
  .optional();

/**
 * Theme palette override.
 *
 * Mirrors `@auw/design-system`'s `TenantThemeOverride` shape — every
 * group/slot is optional so a tenant can rebrand the single colour
 * they care about (typically `brand.base`) without restating the rest
 * of the palette.
 *
 * Slot values stay permissive (`Record<string, string>`) instead of
 * enumerating each key (`surface.canvas`, `surface.raised`, …) so a
 * new design-system palette slot does NOT require a manifest schema
 * bump. `createTheme`'s `mergePalette` silently drops unknown slot
 * ids, so unknown keys are an inert no-op rather than a parse error.
 *
 * The empty-string short-circuit (`.min(1)`) catches the most common
 * config mistake — `brand: { base: '' }` from a half-translated
 * config.json — and surfaces it at parse time instead of letting it
 * through and erasing the colour.
 */
const TenantPaletteSlotSchema = z.record(
  z.string().min(1),
  z.string().min(1),
);

const TenantPaletteOverrideSchema = z
  .object({
    surface: TenantPaletteSlotSchema.optional(),
    text: TenantPaletteSlotSchema.optional(),
    border: TenantPaletteSlotSchema.optional(),
    accent: TenantPaletteSlotSchema.optional(),
    brand: TenantPaletteSlotSchema.optional(),
    feedback: TenantPaletteSlotSchema.optional(),
    usage: TenantPaletteSlotSchema.optional(),
    scrollbar: TenantPaletteSlotSchema.optional(),
  })
  .partial();

const TenantThemeSchema = z
  .object({
    light: TenantPaletteOverrideSchema.optional(),
    dark: TenantPaletteOverrideSchema.optional(),
  })
  .partial()
  .optional();

/**
 * Agent-mode empty-state copy shown above the suggestion carousel
 * (`AgentContent.tsx`). Every field is optional so a tenant can
 * rebrand only `titlePrefix` (the most common need: swap "Askyour"
 * for the corporate product mark) without restating the rotating
 * suffixes or the suggestion array.
 *
 * Slot semantics
 *   - `titlePrefix` — fixed left segment of the H1 (e.g. "Askyour",
 *     "삼성AI"). Pairs with `rotatingSuffixes` to form the animated
 *     wordmark.
 *   - `rotatingSuffixes` — at least one entry to enable the rotation;
 *     a single-item array renders the static suffix without animation
 *     in `EmptyState`.
 *   - `description` — short subtitle line, treated as a literal string
 *     (NOT an i18n key). Tenants that need multi-locale copy can ship
 *     overlays via `/config.json`.
 *   - `suggestions` — replacement carousel entries. When omitted, the
 *     bundled 8-key i18n default (`chat.examplePrompt1..8` +
 *     `chat.exampleBadge1..8`) is used. Setting this to `[]` is also
 *     valid and disables the carousel entirely.
 *   - `iconKey` — opaque identifier mapped to a lucide glyph by the
 *     consumer (`AgentContent.tsx`); unknown keys silently fall back
 *     to the default `Sun` glyph so tenants can ship today and the
 *     icon registry can grow without a schema bump.
 */
const TenantSuggestionEntrySchema = z
  .object({
    text: z.string().min(1),
    badge: z.string().min(1).optional(),
    iconKey: z.string().min(1).optional(),
  });

const TenantAgentEmptyStateSchema = z
  .object({
    titlePrefix: z.string().min(1).optional(),
    rotatingSuffixes: z.array(z.string().min(1)).optional(),
    description: z.string().min(1).optional(),
    suggestions: z.array(TenantSuggestionEntrySchema).optional(),
  })
  .partial()
  .optional();

const TenantChatSchema = z
  .object({
    agentEmptyState: TenantAgentEmptyStateSchema,
  })
  .partial()
  .optional();

/**
 * "전문 에이전트" entry schema. Mirrors `TenantSpecializedAgent` in
 * `types.ts`. `id`, `label`, and `path` are required; everything else
 * is optional so tenants can ship a minimal entry and add labelKey /
 * iconKey / enabled later without a schema rev.
 */
const TenantSpecializedAgentSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  labelKey: z.string().min(1).optional(),
  iconKey: z.string().min(1).optional(),
  path: z.string().min(1),
  enabled: z.boolean().optional(),
});

const TenantSpecializedAgentsSchema = z
  .array(TenantSpecializedAgentSchema)
  .optional();

export const TenantManifestSchema = z.object({
  version: z.literal(1),
  id: z.string().min(1),
  identity: TenantIdentitySchema,
  assets: TenantAssetsSchema,
  features: TenantFeaturesSchema,
  workspace: TenantWorkspaceSchema,
  plugins: TenantPluginsSchema,
  auth: TenantAuthSchema,
  roles: TenantRolesSchema,
  analytics: TenantAnalyticsSchema,
  payments: TenantPaymentsSchema,
  theme: TenantThemeSchema,
  chat: TenantChatSchema,
  specializedAgents: TenantSpecializedAgentsSchema,
});

export type RawTenantManifest = z.infer<typeof TenantManifestSchema>;
